import { Loader2, Trash2 } from "lucide-react";
import {
    entityActionButtonClusterClass,
    primaryEntityActionButtonClass,
    secondaryEntityActionButtonClass,
} from "@/components/ui/entityActionButtonClasses";
import { cn } from "@/lib/utils/helpers";
import type { EntityEditHeaderToolbarProps } from "./EntityEditHeaderToolbar.types";
import {
    iconWrap,
    linkButtonBase,
    linkButtonLabel,
    deleteClass,
} from "./EntityEditHeaderToolbar.constants";

export type {
    EntityEditRelatedLink,
    EntityEditHeaderSaveConfig,
    EntityEditHeaderToolbarProps,
} from "./EntityEditHeaderToolbar.types";

/**
 * "Related pages" quick links and primary form actions (Save / Cancel / Delete) for entity edit screens.
 */
export function EntityEditHeaderToolbar({
    headerActionsDisabled,
    isHeaderSaveLoading,
    isDeleting,
    canEdit,
    canDelete,
    relatedLinks,
    save,
    cancelLabel = "Cancel",
    cancelTestId = "entity-edit-cancel-button",
    deleteTestId = "entity-edit-delete-button",
    onCancel,
    onDelete,
}: EntityEditHeaderToolbarProps) {
    const saveLabel = isHeaderSaveLoading ? "Saving…" : "Save";
    const deleteLabel = isDeleting ? "Deleting…" : "Delete";
    const showFormCluster = canEdit || canDelete;

    return (
        <div className="w-full min-w-0 max-w-full" data-entity-header-toolbar>
            <div
                className={cn(
                    "min-w-0 overflow-hidden rounded-2xl",
                    "border border-slate-200/80 bg-gradient-to-b from-slate-50/98 via-slate-50/90 to-slate-100/50",
                    "shadow-[0_1px_0_0_rgba(255,255,255,0.85)_inset,0_2px_8px_-2px_rgba(15,23,42,0.06),0_1px_1px_0_rgba(15,23,42,0.04)]",
                )}
            >
                <div className="flex w-full min-w-0 flex-col p-1 sm:flex-row sm:items-stretch sm:gap-0 sm:p-1.5">
                    <div
                        className="flex min-w-0 flex-1 flex-col justify-center rounded-xl sm:px-2 sm:py-1.5"
                        role="group"
                        aria-label="Related pages"
                    >
                        <p className="px-0.5 pb-1.5 pl-0.5 text-xs font-medium text-slate-500">Related pages</p>
                        <div className="flex flex-wrap items-stretch gap-x-1.5 gap-y-1.5 sm:gap-x-1.5 sm:gap-y-1.5 sm:pl-0">
                            {relatedLinks.map((link) => {
                                const Icon = link.icon;
                                return (
                                    <button
                                        key={link.key}
                                        type="button"
                                        data-testid={link.testId}
                                        disabled={headerActionsDisabled}
                                        onClick={link.onClick}
                                        className={linkButtonBase}
                                    >
                                        <span className={iconWrap} aria-hidden>
                                            <Icon className="h-4 w-4" strokeWidth={2.25} />
                                        </span>
                                        <span className={linkButtonLabel}>{link.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {showFormCluster && (
                        <>
                            <div
                                className="h-px w-full shrink-0 bg-gradient-to-r from-transparent via-slate-200/90 to-transparent sm:mx-2 sm:h-auto sm:min-h-[2.75rem] sm:w-px sm:self-stretch sm:bg-gradient-to-b"
                                role="separator"
                            />
                            <div
                                className="flex w-full min-w-0 flex-col justify-center sm:w-auto sm:min-w-0"
                                role="group"
                                aria-label="This page"
                            >
                                <p className="px-0.5 pb-1.5 pl-0.5 text-xs font-medium text-slate-500 sm:pl-0">Actions</p>
                                <div className={entityActionButtonClusterClass}>
                                    {canEdit && (
                                        <>
                                            {save.type === "submit" ? (
                                                <button
                                                    type="submit"
                                                    form={save.formId}
                                                    data-testid={save.testId}
                                                    disabled={headerActionsDisabled}
                                                    className={primaryEntityActionButtonClass}
                                                >
                                                    {isHeaderSaveLoading ? (
                                                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                                                    ) : null}
                                                    {saveLabel}
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    data-testid={save.testId}
                                                    disabled={headerActionsDisabled}
                                                    onClick={save.onClick}
                                                    className={primaryEntityActionButtonClass}
                                                >
                                                    {isHeaderSaveLoading ? (
                                                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                                                    ) : null}
                                                    {saveLabel}
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                data-testid={cancelTestId}
                                                disabled={headerActionsDisabled}
                                                onClick={onCancel}
                                                className={secondaryEntityActionButtonClass}
                                            >
                                                {cancelLabel}
                                            </button>
                                        </>
                                    )}
                                    {canDelete && (
                                        <button
                                            type="button"
                                            data-testid={deleteTestId}
                                            disabled={headerActionsDisabled}
                                            onClick={onDelete}
                                            className={deleteClass}
                                        >
                                            {isDeleting ? (
                                                <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
                                            ) : (
                                                <Trash2 className="h-4 w-4 shrink-0" strokeWidth={2} aria-hidden />
                                            )}
                                            {deleteLabel}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
